<template>
  <div class="tutorial-qr">
    <div class="space-y-6">
      <!-- QR Code Technology Explained -->
      <div class="grid md:grid-cols-2 gap-6">
        <!-- What's Inside the QR Code -->
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <IconifyIcon icon="heroicons:lock-closed-20-solid" class="w-5 h-5 text-green-600" />
            What's Inside the QR Code
          </h4>

          <div class="space-y-3">
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <IconifyIcon icon="heroicons:key-20-solid" class="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h5 class="font-medium text-gray-900">Session Description</h5>
                <p class="text-sm text-gray-600">WebRTC offer/answer with codec information</p>
              </div>
            </div>

            <div class="flex items-start gap-3">
              <div class="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <IconifyIcon icon="heroicons:server-20-solid" class="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <h5 class="font-medium text-gray-900">ICE Candidates</h5>
                <p class="text-sm text-gray-600">Network addresses for connection discovery</p>
              </div>
            </div>

            <div class="flex items-start gap-3">
              <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <IconifyIcon icon="heroicons:fingerprint-20-solid" class="w-4 h-4 text-green-600" />
              </div>
              <div>
                <h5 class="font-medium text-gray-900">Secure Fingerprint</h5>
                <p class="text-sm text-gray-600">Cryptographic identity verification</p>
              </div>
            </div>

            <div class="flex items-start gap-3">
              <div class="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <IconifyIcon icon="heroicons:cog-6-tooth-20-solid" class="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <h5 class="font-medium text-gray-900">Connection Config</h5>
                <p class="text-sm text-gray-600">Settings for features and capabilities</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Interactive QR Explorer -->
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <IconifyIcon icon="heroicons:magnifying-glass-20-solid" class="w-5 h-5 text-indigo-600" />
            Interactive QR Explorer
          </h4>

          <div class="space-y-4">
            <!-- QR Structure Visualization -->
            <div class="relative">
              <div class="w-full aspect-square bg-gray-100 rounded-lg p-4">
                <!-- Simulated QR Code Structure -->
                <div class="w-full h-full grid grid-cols-7 gap-1">
                  <!-- Position markers (corners) -->
                  <div class="bg-black rounded-sm"></div>
                  <div class="bg-black rounded-sm"></div>
                  <div class="bg-black rounded-sm"></div>
                  <div class="bg-gray-300 rounded-sm"></div>
                  <div class="bg-black rounded-sm"></div>
                  <div class="bg-black rounded-sm"></div>
                  <div class="bg-black rounded-sm"></div>

                  <!-- Simulated data pattern -->
                  <template v-for="_ in 35" :key="_">
                    <div
                      class="rounded-sm"
                      :class="getRandomQRColor()"
                    ></div>
                  </template>
                </div>
              </div>

              <!-- Hover overlay with info -->
              <div class="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                <div class="bg-white rounded-lg p-4 max-w-xs">
                  <p class="text-sm font-medium text-gray-900">Hover over different areas to see what they contain!</p>
                </div>
              </div>
            </div>

            <!-- Data Size Info -->
            <div class="bg-gray-50 rounded-lg p-4">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-gray-700">QR Code Data Size:</span>
                <span class="text-sm text-gray-600">{{ qrDataSize }} bytes</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                  :style="{ width: `${(qrDataSize / maxQRSize) * 100}%` }"
                ></div>
              </div>
              <p class="text-xs text-gray-500 mt-1">Maximum: {{ maxQRSize }} bytes per QR</p>
            </div>

            <!-- Encryption Status -->
            <div class="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
              <IconifyIcon icon="heroicons:shield-check-20-solid" class="w-5 h-5 text-green-600" />
              <div>
                <p class="text-sm font-medium text-green-900">End-to-End Encrypted</p>
                <p class="text-xs text-green-700">Military-grade AES-256 encryption</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Advanced Features -->
      <div class="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6">
        <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <IconifyIcon icon="heroicons:cog-6-tooth-20-solid" class="w-5 h-5 text-indigo-600" />
          Advanced QR Features
        </h4>

        <div class="grid md:grid-cols-3 gap-4">
          <div class="bg-white rounded-lg p-4">
            <IconifyIcon icon="heroicons:arrows-right-left-20-solid" class="w-8 h-8 text-blue-600 mb-2" />
            <h5 class="font-medium text-gray-900 mb-1">Fragmentation</h5>
            <p class="text-sm text-gray-600">Large data automatically split across multiple QR codes</p>
          </div>

          <div class="bg-white rounded-lg p-4">
            <IconifyIcon icon="heroicons:bolt-20-solid" class="w-8 h-8 text-yellow-600 mb-2" />
            <h5 class="font-medium text-gray-900 mb-1">Error Correction</h5>
            <p class="text-sm text-gray-600">QR codes still work even when partially damaged</p>
          </div>

          <div class="bg-white rounded-lg p-4">
            <IconifyIcon icon="heroicons:clock-20-solid" class="w-8 h-8 text-green-600 mb-2" />
            <h5 class="font-medium text-gray-900 mb-1">Time Expiration</h5>
            <p class="text-sm text-gray-600">QR codes automatically expire for security</p>
          </div>
        </div>
      </div>

      <!-- Try It Interactive -->
      <div class="bg-yellow-50 rounded-lg p-4">
        <div class="flex items-start gap-3">
          <IconifyIcon icon="heroicons:hand-raised-20-solid" class="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h5 class="font-medium text-yellow-900 mb-1">Try It Yourself!</h5>
            <p class="text-sm text-yellow-800">
              In the next step, you'll see how clients scan these QR codes to establish connections.
              The magic happens when the QR code data is decoded and used to create a direct WebRTC connection.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import IconifyIcon from '../IconifyIcon.vue'

// Reactive state
const qrDataSize = ref(1895)
const maxQRSize = ref(2953)

// Methods
const getRandomQRColor = () => {
  const colors = [
    'bg-black',
    'bg-gray-800',
    'bg-gray-700',
    'bg-gray-600',
    'bg-white',
    'bg-gray-200'
  ]
  return colors[Math.floor(Math.random() * colors.length)]
}
</script>

<style scoped>
.tutorial-qr {
  /* Component-specific styles */
}
</style>